import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { AdminGuard } from "@/components/admin/AdminGuard.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import * as r0 from "@/api/karateR0MembersApi.js";
import { ensureKarateR0Session } from "@/api/karateR0SessionBridge.js";
import { useAuth } from "@/context/AuthContext.jsx";

const STATUS_LABELS = { ACTIVE: "Actif", INACTIVE: "Inactif" };
const KIND_LABELS = { ADULT: "Adulte", MINOR: "Mineur" };
const TO_COMPLETE = "À compléter";

function displayOrComplete(value) {
  const text = String(value ?? "").trim();
  return text || TO_COMPLETE;
}

function instructorLabel(member) {
  if (!member.primaryInstructor) return TO_COMPLETE;
  return `${member.primaryInstructor.firstName} ${member.primaryInstructor.lastName}`.trim() || TO_COMPLETE;
}

const EMPTY_CREATE = {
  firstName: "",
  lastName: "",
  dojoId: "",
  emergencyContact: "",
  adminNotes: "",
};

function compareMembers(a, b, sortKey, sortDir) {
  const dir = sortDir === "desc" ? -1 : 1;
  if (sortKey === "dojo") {
    return dir * `${a.dojo?.name ?? ""}`.localeCompare(`${b.dojo?.name ?? ""}`, "fr");
  }
  if (sortKey === "enrolledAt") {
    return dir * (new Date(a.enrolledAt).getTime() - new Date(b.enrolledAt).getTime());
  }
  if (sortKey === "status") {
    return dir * a.status.localeCompare(b.status, "fr");
  }
  if (sortKey === "instructor") {
    return dir * instructorLabel(a).localeCompare(instructorLabel(b), "fr");
  }
  if (sortKey === "grade") {
    return dir * displayOrComplete(a.lastBelt).localeCompare(displayOrComplete(b.lastBelt), "fr");
  }
  if (sortKey === "kind") {
    return dir * displayOrComplete(KIND_LABELS[a.kind] || a.kind).localeCompare(displayOrComplete(KIND_LABELS[b.kind] || b.kind), "fr");
  }
  return dir * `${a.lastName ?? ""}`.localeCompare(`${b.lastName ?? ""}`, "fr");
}

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("fr-CA");
}

export function HanshiMembersR0Content() {
  const auth = useAuth();
  const { ui } = useKarateUi();
  const guard = ui.guard;
  const [sessionReady, setSessionReady] = useState(import.meta.env.VITEST === true || import.meta.env.MODE === "test");
  const [sessionDenied, setSessionDenied] = useState(false);
  const [members, setMembers] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [search, setSearch] = useState("");
  const [dojoFilter, setDojoFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [instructorFilter, setInstructorFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [sortKey, setSortKey] = useState("lastName");
  const [sortDir, setSortDir] = useState("asc");
  const [healthDetail, setHealthDetail] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY_CREATE });
  const [editId, setEditId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_CREATE, expectedUpdatedAt: "" });
  const [rowBusy, setRowBusy] = useState(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [editBusy, setEditBusy] = useState(false);

  useEffect(() => {
    if (sessionReady || sessionDenied) return;
    let active = true;
    ensureKarateR0Session({ auth }).then((result) => {
      if (!active) return;
      if (result.ok) {
        setSessionReady(true);
        return;
      }
      setSessionDenied(true);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [auth, sessionReady, sessionDenied]);

  const loadData = useCallback(async () => {
    if (!sessionReady) return;
    setLoading(true);
    const [listRes, dojoRes] = await Promise.all([
      r0.fetchAdminMembers({
        search: search.trim() || undefined,
        dojoId: dojoFilter || undefined,
        status: statusFilter || undefined,
      }),
      r0.fetchDojosForSelect(),
    ]);
    setLoading(false);
    if (listRes.status === 401) {
      setSessionDenied(true);
      return;
    }
    if (!listRes.ok) {
      setError(listRes.body?.error ?? "Impossible de charger les membres.");
      return;
    }
    setError("");
    setMembers(listRes.body.members ?? []);
    if (dojoRes.ok) setDojos(dojoRes.body.dojos ?? []);
  }, [sessionReady, search, dojoFilter, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sortedMembers = useMemo(() => {
    return [...members]
      .filter((m) => {
        if (instructorFilter && instructorLabel(m) !== instructorFilter) return false;
        if (kindFilter && (m.kind || "") !== kindFilter) return false;
        if (gradeFilter && displayOrComplete(m.lastBelt) !== gradeFilter) return false;
        return true;
      })
      .sort((a, b) => compareMembers(a, b, sortKey, sortDir));
  }, [members, sortKey, sortDir, instructorFilter, kindFilter, gradeFilter]);

  const instructorOptions = useMemo(
    () => [...new Set(members.map(instructorLabel).filter((n) => n && n !== TO_COMPLETE))].sort((a, b) => a.localeCompare(b, "fr")),
    [members],
  );
  const gradeOptions = useMemo(
    () => [...new Set(members.map((m) => displayOrComplete(m.lastBelt)))].sort((a, b) => a.localeCompare(b, "fr")),
    [members],
  );

  const detailMember = useMemo(
    () => sortedMembers.find((m) => m.id === detailId) ?? members.find((m) => m.id === detailId) ?? null,
    [sortedMembers, members, detailId],
  );

  useEffect(() => {
    if (!detailId || !sessionReady) {
      setHealthDetail(null);
      return;
    }
    let active = true;
    r0.fetchAdminMemberHealth(detailId).then((res) => {
      if (!active) return;
      setHealthDetail(res.ok ? res.body.health : null);
    });
    return () => {
      active = false;
    };
  }, [detailId, sessionReady]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function sortIndicator(key) {
    return sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";
  }

  async function handleCreate(event) {
    event.preventDefault();
    if (createBusy) return;
    setNotice("");
    setError("");
    setCreateBusy(true);
    const result = await r0.createMember({
      firstName: createForm.firstName.trim(),
      lastName: createForm.lastName.trim(),
      dojoId: createForm.dojoId,
      emergencyContact: createForm.emergencyContact.trim() || null,
      adminNotes: createForm.adminNotes.trim() || null,
    });
    setCreateBusy(false);
    if (!result.ok) {
      setError(result.body?.error ?? "Création impossible.");
      return;
    }
    setNotice("Membre créé.");
    setCreateForm({ ...EMPTY_CREATE });
    setCreateOpen(false);
    await loadData();
  }

  function startEdit(member) {
    setEditId(member.id);
    setDetailId(null);
    setEditForm({
      firstName: member.firstName,
      lastName: member.lastName,
      dojoId: member.dojoId,
      emergencyContact: member.emergencyContact ?? "",
      adminNotes: member.adminNotes ?? "",
      expectedUpdatedAt: member.updatedAt,
    });
  }

  async function handleUpdate(event) {
    event.preventDefault();
    if (!editId || editBusy) return;
    setNotice("");
    setError("");
    setEditBusy(true);
    const result = await r0.updateMember(editId, {
      firstName: editForm.firstName.trim(),
      lastName: editForm.lastName.trim(),
      dojoId: editForm.dojoId,
      emergencyContact: editForm.emergencyContact.trim() || null,
      adminNotes: editForm.adminNotes.trim() || null,
      expectedUpdatedAt: editForm.expectedUpdatedAt,
    });
    setEditBusy(false);
    if (!result.ok) {
      setError(result.body?.error ?? "Modification impossible.");
      return;
    }
    setNotice("Membre modifié.");
    setEditId(null);
    await loadData();
  }

  async function toggleStatus(member) {
    if (!window.confirm(member.status === "ACTIVE" ? "Désactiver ce membre ?" : "Réactiver ce membre ?")) {
      return;
    }
    setRowBusy(member.id);
    setNotice("");
    setError("");
    const next = member.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const result = await r0.setMemberStatus(member.id, next, member.updatedAt);
    setRowBusy(null);
    if (!result.ok) {
      setError(result.body?.error ?? "Changement de statut impossible.");
      return;
    }
    setNotice(next === "ACTIVE" ? "Membre réactivé." : "Membre désactivé.");
    await loadData();
  }

  if (sessionDenied) {
    return (
      <p data-testid="members-session-denied">
        {guard.denied} <Link to="/connexion">{guard.login}</Link>
      </p>
    );
  }

  if (!sessionReady) {
    return <p data-testid="members-loading">Chargement…</p>;
  }

  return (
    <>
      <div className="accounts-toolbar" data-testid="members-toolbar">
        <input
          type="search"
          placeholder="Rechercher…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="members-search"
          aria-label="Rechercher un membre"
        />
        <select
          value={dojoFilter}
          onChange={(e) => setDojoFilter(e.target.value)}
          data-testid="members-dojo-filter"
          aria-label="Filtrer par dojo"
        >
          <option value="">Tous les dojos</option>
          {dojos.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name} ({d.city})
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="members-status-filter"
          aria-label="Filtrer par statut"
        >
          <option value="">Tous les statuts</option>
          <option value="ACTIVE">Actifs</option>
          <option value="INACTIVE">Inactifs</option>
        </select>
        <select
          value={instructorFilter}
          onChange={(e) => setInstructorFilter(e.target.value)}
          data-testid="members-instructor-filter"
          aria-label="Filtrer par instructeur"
        >
          <option value="">Tous les instructeurs</option>
          {instructorOptions.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          data-testid="members-kind-filter"
          aria-label="Filtrer adulte ou mineur"
        >
          <option value="">Adulte / mineur</option>
          <option value="ADULT">Adulte</option>
          <option value="MINOR">Mineur</option>
        </select>
        <select
          value={gradeFilter}
          onChange={(e) => setGradeFilter(e.target.value)}
          data-testid="members-grade-filter"
          aria-label="Filtrer par grade"
        >
          <option value="">Tous les grades</option>
          {gradeOptions.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setSearch(""); setDojoFilter(""); setStatusFilter(""); setInstructorFilter(""); setKindFilter(""); setGradeFilter(""); }} data-testid="members-reset-filters">
          Réinitialiser
        </button>
        <button type="button" className="btn btn--primary btn--sm" onClick={() => setCreateOpen((v) => !v)} data-testid="members-create-toggle">
          {createOpen ? "Annuler" : "Nouveau membre"}
        </button>
      </div>

      {notice ? <p className="form-notice" role="status">{notice}</p> : null}
      {error ? <p className="form-error" role="alert" data-testid="members-error">{error}</p> : null}

      {createOpen ? (
        <form className="create-account-form" onSubmit={handleCreate} data-testid="members-create-form">
          <h2>Nouveau membre</h2>
          <label>Prénom *<input required value={createForm.firstName} onChange={(e) => setCreateForm((f) => ({ ...f, firstName: e.target.value }))} data-testid="members-create-firstName" /></label>
          <label>Nom *<input required value={createForm.lastName} onChange={(e) => setCreateForm((f) => ({ ...f, lastName: e.target.value }))} data-testid="members-create-lastName" /></label>
          <label>Dojo *
            <select required value={createForm.dojoId} onChange={(e) => setCreateForm((f) => ({ ...f, dojoId: e.target.value }))} data-testid="members-create-dojo">
              <option value="">Choisir…</option>
              {dojos.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label>Contact d&apos;urgence<textarea value={createForm.emergencyContact} onChange={(e) => setCreateForm((f) => ({ ...f, emergencyContact: e.target.value }))} data-testid="members-create-emergency" /></label>
          <label>Notes administratives<textarea value={createForm.adminNotes} onChange={(e) => setCreateForm((f) => ({ ...f, adminNotes: e.target.value }))} data-testid="members-create-notes" /></label>
          <button type="submit" className="btn btn--primary" disabled={createBusy} data-testid="members-create-submit">{createBusy ? "Création…" : "Créer"}</button>
        </form>
      ) : null}

      {editId ? (
        <form className="create-account-form" onSubmit={handleUpdate} data-testid="members-edit-form">
          <h2>Modifier le membre</h2>
          <label>Prénom *<input required value={editForm.firstName} onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))} data-testid="members-edit-firstName" /></label>
          <label>Nom *<input required value={editForm.lastName} onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))} data-testid="members-edit-lastName" /></label>
          <label>Dojo *
            <select required value={editForm.dojoId} onChange={(e) => setEditForm((f) => ({ ...f, dojoId: e.target.value }))} data-testid="members-edit-dojo">
              {dojos.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label>Contact d&apos;urgence<textarea value={editForm.emergencyContact} onChange={(e) => setEditForm((f) => ({ ...f, emergencyContact: e.target.value }))} data-testid="members-edit-emergency" /></label>
          <label>Notes administratives<textarea value={editForm.adminNotes} onChange={(e) => setEditForm((f) => ({ ...f, adminNotes: e.target.value }))} data-testid="members-edit-notes" /></label>
          <button type="submit" className="btn btn--primary" disabled={editBusy} data-testid="members-edit-submit">{editBusy ? "Enregistrement…" : "Enregistrer"}</button>
          <button type="button" className="btn btn--secondary" disabled={editBusy} onClick={() => setEditId(null)}>Annuler</button>
        </form>
      ) : null}

      {detailMember ? (
        <section className="member-detail-card" data-testid="member-detail-card">
          <h2>Fiche membre</h2>
          <h3>Identité</h3>
          <p><strong>{detailMember.firstName} {detailMember.lastName}</strong></p>
          <p>Genre / naissance : {displayOrComplete(detailMember.birthDate ? formatDate(detailMember.birthDate) : "")}</p>
          <h3>Coordonnées</h3>
          <p>Courriel : {displayOrComplete(detailMember.contactEmail || detailMember.email)}</p>
          <p>Adresse : {displayOrComplete(detailMember.address)}</p>
          <h3>Dojo</h3>
          <p>{detailMember.dojo ? `${detailMember.dojo.name} (${detailMember.dojo.city})` : TO_COMPLETE}</p>
          <h3>Instructeur</h3>
          <p>{instructorLabel(detailMember)}</p>
          <h3>Grade / progression</h3>
          <p>{displayOrComplete(detailMember.lastBelt)}{detailMember.lastBeltDate ? ` — ${formatDate(detailMember.lastBeltDate)}` : ""}</p>
          <h3>Parent/responsable</h3>
          <p>{displayOrComplete(detailMember.emergencyContact)}</p>
          <h3>Adhésion</h3>
          <p>Statut : {STATUS_LABELS[detailMember.status]} · {KIND_LABELS[detailMember.kind] || displayOrComplete(detailMember.kind)}</p>
          <p>Source : {displayOrComplete(detailMember.source)}</p>
          <h3>Santé — accès protégé</h3>
          {healthDetail?.payload ? (
            <dl data-testid="member-r0-health">
              {Object.entries(healthDetail.payload).map(([key, val]) => (
                <React.Fragment key={key}>
                  <dt>{key}</dt>
                  <dd>{displayOrComplete(typeof val === "string" ? val : JSON.stringify(val))}</dd>
                </React.Fragment>
              ))}
            </dl>
          ) : (
            <p data-testid="member-r0-health-empty">{TO_COMPLETE}</p>
          )}
          <h3>Historique / source</h3>
          <p>Inscription : {formatDate(detailMember.enrolledAt)}</p>
          <p>Notes admin : {displayOrComplete(detailMember.adminNotes)}</p>
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setDetailId(null)} data-testid="member-detail-close">Fermer</button>
        </section>
      ) : null}

      {loading ? <p data-testid="members-loading">Chargement…</p> : null}
      {!loading && sortedMembers.length === 0 ? <p className="accounts-empty" data-testid="members-empty">Aucun membre trouvé.</p> : null}

      {!loading && sortedMembers.length > 0 ? (
        <div className="accounts-table-wrap">
          <table className="accounts-table" data-testid="members-table">
            <thead>
              <tr>
                <th><button type="button" className="accounts-table__sort" onClick={() => toggleSort("lastName")}>Nom{sortIndicator("lastName")}</button></th>
                <th><button type="button" className="accounts-table__sort" onClick={() => toggleSort("dojo")}>Dojo{sortIndicator("dojo")}</button></th>
                <th><button type="button" className="accounts-table__sort" onClick={() => toggleSort("instructor")}>Instructeur{sortIndicator("instructor")}</button></th>
                <th><button type="button" className="accounts-table__sort" onClick={() => toggleSort("grade")}>Grade{sortIndicator("grade")}</button></th>
                <th><button type="button" className="accounts-table__sort" onClick={() => toggleSort("kind")}>Type{sortIndicator("kind")}</button></th>
                <th><button type="button" className="accounts-table__sort" onClick={() => toggleSort("status")}>Statut{sortIndicator("status")}</button></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map((member) => (
                <tr key={member.id} data-testid={`member-row-${member.id}`}>
                  <td>{member.firstName} {member.lastName}</td>
                  <td>{member.dojo ? `${member.dojo.name} (${member.dojo.city})` : TO_COMPLETE}</td>
                  <td>{instructorLabel(member)}</td>
                  <td>{displayOrComplete(member.lastBelt)}</td>
                  <td>{KIND_LABELS[member.kind] || displayOrComplete(member.kind)}</td>
                  <td data-testid={`member-status-${member.id}`}>{STATUS_LABELS[member.status]}</td>
                  <td className="accounts-table__actions">
                    <button type="button" className="btn btn--ghost btn--sm" disabled={rowBusy === member.id} onClick={() => { setDetailId(member.id); setEditId(null); }} data-testid={`member-detail-${member.id}`}>Fiche</button>
                    <button type="button" className="btn btn--secondary btn--sm" disabled={rowBusy === member.id} onClick={() => startEdit(member)} data-testid={`member-edit-${member.id}`}>Modifier</button>
                    <button type="button" className="btn btn--ghost btn--sm" disabled={rowBusy === member.id} onClick={() => toggleStatus(member)} data-testid={`member-toggle-status-${member.id}`}>{member.status === "ACTIVE" ? "Désactiver" : "Réactiver"}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

export function HanshiMembersR0Page() {
  const { ui } = useKarateUi();
  const page = ui.adminPages.etudiants;

  return (
    <AdminGuard>
      <PageSection
        title={page.titleR0 ?? "Membres / Étudiants"}
        subtitle={page.subtitleR0 ?? page.subtitle}
        testId="page-hanshi-members"
      >
        <HanshiMembersR0Content />
      </PageSection>
    </AdminGuard>
  );
}
